import type { DealCaseV1, DealCommitmentV1, DealMeasurementBindingV1, PromiseBandSemantics, PromiseKind, PromiseSetDraft, PromiseSetV1, ValueBasis } from './ma-deal-case';
import { approvePromiseSet } from './ma-deal-case.functions';
import { type FourSeriesComparableEnvelope, type FourSeriesComparableIdentity, type FourSeriesEvidenceBasis, type FourSeriesMilestoneState, type FourSeriesPoint } from './four-series-assessment';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';
import type { DealCommitmentRef, GovernedSeatRef, PromiseSetRef, ReceiptRef } from './ma-deal-refs';
import {
  canonicalDealSourceClass,
  maDealPackAdmissionVerdict,
  MA_DEAL_VALUE_BASIS_UNATTESTED,
} from './ma-deal-pack.validation';

export {
  maDealValueBasisGapState,
} from './ma-deal-pack.validation';
export type { MaDealValueBasisGapState } from './ma-deal-pack.validation';

type CompilerStampedCommitmentField = 'schemaVersion' | 'dealRef' | 'promiseSetRef' | 'supersedesCommitmentRef';
const COMPILER_STAMPED_COMMITMENT_FIELDS: readonly CompilerStampedCommitmentField[] = ['schemaVersion', 'dealRef', 'promiseSetRef', 'supersedesCommitmentRef'];

export type MaDealPackUnderwritingRow = Readonly<Omit<DealCommitmentV1, CompilerStampedCommitmentField>>;

/**
 * One realization point in the admitted pack. The four-series assessment owns
 * the point's evidence basis and milestone state; this wrapper carries the
 * canonical source-class declaration separately, so `attested` is never
 * smuggled into the source-class enum.
 */
export type MaDealRealizationPointV1 = Readonly<FourSeriesPoint & {
  evidenceBasis: FourSeriesEvidenceBasis;
  milestoneState: FourSeriesMilestoneState;
  sourceClass: DecisionAgentSourceClass | null;
}>;

export type MaDealValueBasisGapV1 = Readonly<{ code: typeof MA_DEAL_VALUE_BASIS_UNATTESTED; detail: string }>;

/** Identity whose governed value basis may be null while its attestation is pending. */
export type MaDealRealizationComparisonBasisV1 = Readonly<
  Omit<FourSeriesComparableIdentity, 'valueBasis'> & { valueBasis: ValueBasis | null }
>;

export type MaDealRealizationRecordV1 = Readonly<
  Omit<FourSeriesComparableEnvelope<MaDealRealizationPointV1>, 'comparisonBasis'> & {
    commitmentRef: DealCommitmentRef;
    forecastGap: Readonly<{ code: 'forecast_not_supplied'; detail: string }>;
    comparisonBasis: MaDealRealizationComparisonBasisV1;
    valueBasisGap?: MaDealValueBasisGapV1 | null;
  }
>;

export type CompileMaDealPackInput = Readonly<{
  dealCase: DealCaseV1;
  promiseSetRef: PromiseSetRef;
  version: number;
  approvedAt: string;
  approvedBy: GovernedSeatRef;
  sourceDocumentRefs: readonly ReceiptRef[];
  promiseKind: PromiseKind;
  valueBasis: ValueBasis;
  bandSemantics: PromiseBandSemantics;
  underwritingRows: readonly MaDealPackUnderwritingRow[];
  measurementBindings?: readonly DealMeasurementBindingV1[];
  realization?: readonly MaDealRealizationRecordV1[];
}>;

export type CompiledMaDealPack = Readonly<{
  dealCase: DealCaseV1;
  promiseSet: PromiseSetV1;
  commitments: readonly DealCommitmentV1[];
  measurementBindings: readonly DealMeasurementBindingV1[];
  realization: readonly MaDealRealizationRecordV1[];
}>;


export function compileMaDealPack(input: CompileMaDealPackInput): CompiledMaDealPack {
  assertCompileInputShape(input);
  const commitments = input.underwritingRows.map((row) => compileCommitment(input, row));
  const measurementBindings = input.measurementBindings ?? [];
  const rawRealization = input.realization ?? [];
  const draft: PromiseSetDraft = {
    schemaVersion: 'deal_promise_set_v1',
    promiseSetId: input.promiseSetRef,
    dealRef: input.dealCase.dealRef,
    version: input.version,
    approvedAt: input.approvedAt,
    approvedBy: input.approvedBy,
    sourceDocumentRefs: [...input.sourceDocumentRefs],
    bandSemantics: { ...input.bandSemantics, bands: [...input.bandSemantics.bands] },
    reportingCurrencyRef: input.dealCase.reportingCurrencyRef,
    fiscalCalendarRef: input.dealCase.fiscalCalendarRef,
    valueBasis: input.valueBasis,
    promiseKind: input.promiseKind,
    governanceState: 'draft',
    commitmentRefs: commitments.map((commitment) => commitment.commitmentRef),
    commitments,
  };
  const promiseSet = approvePromiseSet(draft);
  const admission = maDealPackAdmissionVerdict({
    dealCase: input.dealCase,
    promiseSets: [promiseSet],
    measurementBindings,
    realization: rawRealization,
  });
  if (!admission.ok) throw new Error(`Invalid M&A deal pack:\n- ${admission.message}`);
  const realization = rawRealization.map(cloneRealizationRecord);
  return { dealCase: input.dealCase, promiseSet, commitments: promiseSet.commitments, measurementBindings, realization };
}

function assertCompileInputShape(input: CompileMaDealPackInput): void {
  const errors: string[] = [];
  if (!Array.isArray(input.underwritingRows)) errors.push('underwritingRows must be an array');
  for (const [index, row] of (Array.isArray(input.underwritingRows) ? input.underwritingRows : []).entries()) {
    for (const field of COMPILER_STAMPED_COMMITMENT_FIELDS) {
      if (isRecord(row) && Object.prototype.hasOwnProperty.call(row, field)) errors.push(`underwritingRows[${index}].${field} is compiler-stamped and must not be supplied`);
    }
  }
  if (input.measurementBindings !== undefined && !Array.isArray(input.measurementBindings)) errors.push('measurementBindings must be an array');
  if (input.realization !== undefined && !Array.isArray(input.realization)) errors.push('realization must be an array');
  if (errors.length) throw new Error(`Invalid M&A deal pack:\n- ${errors.join('\n- ')}`);
}

function compileCommitment(input: CompileMaDealPackInput, row: unknown): DealCommitmentV1 {
  const source = isRecord(row) ? row : {};
  return {
    schemaVersion: 'deal_commitment_v1',
    commitmentRef: source.commitmentRef as DealCommitmentV1['commitmentRef'],
    displayLabel: source.displayLabel as string,
    dealRef: input.dealCase.dealRef,
    promiseSetRef: input.promiseSetRef,
    commitmentType: source.commitmentType as DealCommitmentV1['commitmentType'],
    benefitDisposition: source.benefitDisposition as DealCommitmentV1['benefitDisposition'],
    recurrence: source.recurrence as DealCommitmentV1['recurrence'],
    certaintyClass: source.certaintyClass as DealCommitmentV1['certaintyClass'],
    baselineRef: source.baselineRef as DealCommitmentV1['baselineRef'],
    targetByBand: source.targetByBand as DealCommitmentV1['targetByBand'],
    targetDateRef: source.targetDateRef as DealCommitmentV1['targetDateRef'],
    realizationCurveRef: source.realizationCurveRef as DealCommitmentV1['realizationCurveRef'],
    costToAchieve: source.costToAchieve as DealCommitmentV1['costToAchieve'],
    accountableSeatRef: source.accountableSeatRef as DealCommitmentV1['accountableSeatRef'],
    thesisOutcomeRefs: source.thesisOutcomeRefs as DealCommitmentV1['thesisOutcomeRefs'],
    measurementBindingRef: source.measurementBindingRef as DealCommitmentV1['measurementBindingRef'],
    bandSemantics: source.bandSemantics as DealCommitmentV1['bandSemantics'],
  };
}

function cloneRealizationRecord(record: MaDealRealizationRecordV1): MaDealRealizationRecordV1 {
  return {
    ...record,
    series: {
      originalPromise: record.series.originalPromise.map(cloneRealizationPoint),
      currentPlan: record.series.currentPlan.map(cloneRealizationPoint),
      actual: record.series.actual.map(cloneRealizationPoint),
      forecast: record.series.forecast.map(cloneRealizationPoint),
    },
    forecastGap: { ...record.forecastGap },
  };
}

function cloneRealizationPoint(point: MaDealRealizationPointV1): MaDealRealizationPointV1 {
  return {
    asOf: point.asOf,
    value: point.value,
    evidenceBasis: point.evidenceBasis,
    milestoneState: point.milestoneState ? { ...point.milestoneState } : null,
    sourceClass: canonicalDealSourceClass(point.sourceClass),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
